import { useState } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6etboeqd308q1a0ara0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-24 bg-deep-black">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-serif text-4xl lg:text-5xl text-white mb-4">
            Send Us a Message
          </h2>
          <p className="text-gray-400 text-lg">
            Fill out the form below and we'll get back to you as soon as possible
          </p>
        </div>

        <form 
          id="contact-form" 
          onSubmit={handleSubmit} 
          data-readdy-form
          className="bg-forest-green rounded-2xl p-8 lg:p-12 shadow-xl"
        >
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-white font-medium mb-2">
                Full Name <span className="text-copper">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-deep-black/50 border border-copper/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-copper transition-colors duration-200"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-white font-medium mb-2">
                Email Address <span className="text-copper">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-deep-black/50 border border-copper/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-copper transition-colors duration-200"
                placeholder="john@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-white font-medium mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-deep-black/50 border border-copper/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-copper transition-colors duration-200"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-white font-medium mb-2">
                Message <span className="text-copper">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={6}
                className="w-full px-4 py-3 bg-deep-black/50 border border-copper/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-copper transition-colors duration-200 resize-none"
                placeholder="Tell us how we can help you..."
              ></textarea>
              <p className="text-gray-500 text-sm mt-2">
                {formData.message.length}/500 characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-copper text-deep-black font-semibold rounded-full hover:bg-copper/90 transition-all duration-300 hover:shadow-lg hover:shadow-copper/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-center">
                  Thank you! Your message has been sent successfully.
                </p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-center">
                  Something went wrong. Please try again or call us directly.
                </p>
              </div>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
